import { AuthenticateSmartcheckSessionDto } from '../../../types/authenticateSmartcheckSessionDto'
import { History } from 'history'
import { FormikProps, FormikErrors, getIn, FormikValues } from 'formik'
import { Region } from '@monthio/react-components'
import { ModuleData } from '#providers/moduleProvider'
import {
	ModuleDefinitionType,
	SmartCheckConfigurationDto,
} from '../../../types/authenticateSmartcheckSessionDto'
import {
	getFixedExpensesSplitQuestion,
	getFixedExpensesSplitDifferentAmountsSubquestion,
	ExpenseContributionAnswerType,
} from '../Components/HouseholdSingleCohabitantQuestionnaire/FixedExpensesSplitQuestion'
import { ValidateCprAgainstServerFunction } from '../Components/personalInformation/validators/types'
import { ClientInputsType } from '../../../types'

export enum HousingStatus {
	Rent = 'rent',
	OwnerOccupied = 'ownerOccupied',
	HousingCooperative = 'housingCooperative',
	LivingAtHome = 'livingAtHome',
	Other = 'other',
}

export enum CivilStatus {
	None = 'none',
	Married = 'married',
	Alone = 'alone',
}

export enum ChildrenAgeGroup {
	Unknown = 'unknown',
	AgeGroup0To1 = 'ageGroup0To1',
	AgeGroup2To6 = 'ageGroup2To6',
	AgeGroup7To17 = 'ageGroup7To17',
	AgeGroupOver18 = 'ageGroupOver18',
}

export enum CarStatus {
	None = 'none',
	Private = 'private',
	Lease = 'lease',
	Loan = 'loan',
}

export enum EmploymentStatus {
	None = 'none',
	Employee = 'employee',
	SelfEmployed = 'selfEmployed',
	Pensioner = 'pensioner',
	Student = 'student',
}

export type ConsumerInputsRequestDto = {
	householdInformation?: DefaultConsumerInputsDto
	householdSingleCohabitantInformation?: HouseholdSingleCohabitantInputs
	personalInformation?: PersonalInformation
	carInformation?: CarLenderInputs
	homeInformation?: HomeLenderInputs
	mbfInformation?: MbfInputs
}

export type DefaultConsumerInputsDto = {
	children: { ageGroup: ChildrenAgeGroup }[]
	civilStatus: CivilStatus
	housingStatus?: HousingStatus | null
}
// export type DefaultConsumerInputs = {
// 	children: { ageGroup: ChildrenAgeGroup }[]
// 	civilStatus: CivilStatus
// 	housingStatus?: HousingStatus | null
// }

export type HouseholdSingleCohabitantInputs = {
	expenseContribution: number
}

export type CarLenderInputs = {
	acquiringExtraCar: boolean | null
	currentMonthlyCarCost: number | null
	carStatus: CarStatus | null
	redemptionAmount: number | null
	hasHadCarBefore: boolean | null
	hasExpensesToMultipleCars: boolean | null
	insurance: number | null
}

export type HomeLenderInputs = {
	acquiringExtraHome: boolean | null
	hasExpensesToMultipleHomes: boolean | null
	insurance: number | null
}

export type MbfInputs = {
	employmentStatus: EmploymentStatus
	isEmploymentAnnounced: boolean | null
	selfEmployedSince: Date | null
	housingChangesLast5Years: number | null
}

export type PersonalInformation = {
	cprNumber: string
	phoneNumber: string
	email: string
	nationalities: string[]
}

export enum LoanType {
	None = 'none',
	CarLoan = 'carLoan',
	CarLeasing = 'carLeasing',
	ConsumerLoan = 'consumerLoan',
	HomeLoan = 'homeLoan',
}

export type FormStatus = {
	answered: boolean
	moduleDefinitionType: ModuleDefinitionType
	shouldSkip: boolean
}

export type QuestionnaireModuleConfig = {
	loanType: ClientInputsType | undefined
	setConsumerInputsStepIndex: (index: number) => void
	moduleIds: ModuleDefinitionType[]
	idDocumentNationalityExemptions: string[] | null
	questionnaireStatus: FormStatus[]
	clientInputCivilStatus: CivilStatus | undefined
	regionId: string
	validateCprAgainstServer: ValidateCprAgainstServerFunction
}

export type QuestionnaireModuleData = ModuleData<
	any,
	DefaultConsumerInputs, // ConsumerInputsRequestDto
	QuestionnaireModuleConfig
>

export type DefaultConsumerInputs = {
	familyInformation?: FamilyInformation
	householdSingleCohabitantInformation?: FormikValues
	personalInformation?: PersonalInformation
	carLenderInputs?: CarLenderInputs
	homeLenderInputs?: HomeLenderInputs
	mbfInputs?: MbfInputs
	uploadedFiles?: File[]
	idDocumentNationalityExemptions?: string[] | null
}

export const ModuleDefinitionQuestionnaire = {
	PersonalInformationQuestionnaire: 'personalInformationQuestionnaire',
	PersonalInformationExtendedQuestionnaire:
		'personalInformationExtendedQuestionnaire',
	PersonalInformationCprOnlyQuestionnaire:
		'personalInformationCprOnlyQuestionnaire',
	HouseholdQuestionnaire: 'householdQuestionnaire',
	HouseholdSingleCohabitantQuestionnaire:
		'householdSingleCohabitantQuestionnaire',
	AssetQuestionnaire: 'assetQuestionnaire',
	CarExtendedQuestionnaire: 'carExtendedQuestionnaire',
	MbfQuestionnaire: 'mbfQuestionnaire',
} as const

export type ModuleDefinitionQuestionnaireType =
	(typeof ModuleDefinitionQuestionnaire)[keyof typeof ModuleDefinitionQuestionnaire]

export type QuestionnaireStep = {
	validation: ({
		errors,
		regionId,
		values,
		caseConfiguration,
	}: QuestionnaireStepValidationParameters) => Promise<
		FormikErrors<DefaultConsumerInputs>
	>
	component: ({
		formProps,
		regionId,
	}: {
		formProps: FormikProps<DefaultConsumerInputs>
		regionId: Region
	}) => JSX.Element
}

export interface QuestionnaireStepValidationParameters {
	values: DefaultConsumerInputs
	errors: any
	regionId: string
	validateCprAgainstServer: ValidateCprAgainstServerFunction
	caseConfiguration?: SmartCheckConfigurationDto
}

export type FamilyInformation = {
	civilStatus: CivilStatus
	childrenCount: number
	housingStatus: HousingStatus | null
	childrenAges: ChildrenAgeGroup[]
}

export type ConsumerInputsPageProps = {
	smartCheckSession?: AuthenticateSmartcheckSessionDto
	history: History
	createConsumerInputs: (inputs: DefaultConsumerInputs) => void
	loanType?: LoanType
	setConsumerInputsStepIndex: (value: number) => void
}

export const transformToRequestDto = (
	inputs: DefaultConsumerInputs,
): ConsumerInputsRequestDto => {
	let requestPayload: ConsumerInputsRequestDto = {}

	if (inputs.familyInformation != null) {
		requestPayload.householdInformation = {
			civilStatus: inputs.familyInformation.civilStatus,
			children: inputs.familyInformation.childrenAges.map((x) => {
				return { ageGroup: x }
			}),
			housingStatus: inputs.familyInformation.housingStatus,
		}
	}

	if (inputs.personalInformation) {
		requestPayload.personalInformation = inputs.personalInformation
		// Needed until we can release updates for V5 outputs, that doesn't save consumer inputs directly
		if (inputs.personalInformation.cprNumber) {
			requestPayload.personalInformation.cprNumber =
				inputs.personalInformation.cprNumber
		}
	}

	if (inputs.carLenderInputs) {
		requestPayload = {
			...requestPayload,
			carInformation: inputs.carLenderInputs,
		}
	}

	if (inputs.mbfInputs) {
		requestPayload = {
			...requestPayload,
			mbfInformation: inputs.mbfInputs,
		}
	}

	if (inputs.homeLenderInputs) {
		requestPayload = {
			...requestPayload,
			homeInformation: inputs.homeLenderInputs,
		}
	}

	if (inputs.householdSingleCohabitantInformation) {
		requestPayload = {
			...requestPayload,
			householdSingleCohabitantInformation:
				mapQuestionWithAnswersToHouseholdSingleCohabitantInformation(
					inputs.householdSingleCohabitantInformation,
				),
		}
	}

	return requestPayload
}

export const mapQuestionWithAnswersToHouseholdSingleCohabitantInformation = (
	values: FormikValues,
): HouseholdSingleCohabitantInputs | undefined => {
	const fixedExpensesSplitQuestion = getFixedExpensesSplitQuestion()
	const mainQuestionAnswer = getIn(
		values,
		fixedExpensesSplitQuestion.formFieldKey,
	)
	const differentAmountsSplitSubquestion =
		getFixedExpensesSplitDifferentAmountsSubquestion()
	const differentAmountsSplitSubquestionAnswer = getIn(
		values,
		differentAmountsSplitSubquestion.formFieldKey,
	)

	if (mainQuestionAnswer === undefined) {
		return undefined
	}

	switch (mainQuestionAnswer) {
		case ExpenseContributionAnswerType.ApplicantPaysAll:
			return { expenseContribution: 1 }
		case ExpenseContributionAnswerType.PartnerPaysAll:
			return { expenseContribution: 0 }
		case ExpenseContributionAnswerType.Equal:
			return { expenseContribution: 0.5 }
		case ExpenseContributionAnswerType.DifferentAmounts:
			return differentAmountsSplitSubquestionAnswer !== undefined &&
				typeof differentAmountsSplitSubquestionAnswer === 'number'
				? { expenseContribution: differentAmountsSplitSubquestionAnswer / 100 }
				: undefined

		default:
			return undefined
	}
}
